try {
            if (process.env.TOKENS_FEATURE_FLAG === 'true') require('./index-new.css');
            else require('./index.css');
          } catch (e) {
            if (e.code !== 'MODULE_NOT_FOUND') {
              throw e; // Re-throw unexpected errors
            }
            console.warn('index.css not found, proceeding without it.');
          }
          

import { jsx, jsxs } from 'react/jsx-runtime';
import { Button } from '@design-system/button';
import classNames from 'classnames';

const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
};
const calculateItems = ({ totalCount, pageSize, currentPage, ellipsis, siblingCount, visibleItemsNumber, }) => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;
    const totalPaginationPositions = 2 * siblingCount + visibleItemsNumber;
    if (totalPaginationPositions >= totalPageCount)
        return range(firstPageIndex, lastPageIndex);
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);
    const shouldShowLeftEllipsis = leftSiblingIndex > 2;
    const shouldShowRightEllipsis = rightSiblingIndex < totalPageCount - 2;
    if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
        return [
            firstPageIndex,
            ellipsis,
            ...range(leftSiblingIndex, rightSiblingIndex),
            ellipsis,
            lastPageIndex,
        ];
    }
    if (!shouldShowLeftEllipsis)
        return [
            ...range(firstPageIndex, siblingCount + currentPage),
            ellipsis,
            totalPageCount,
        ];
    if (!shouldShowRightEllipsis) {
        const pagesToEnd = totalPageCount - currentPage;
        return [
            firstPageIndex,
            ellipsis,
            ...range(totalPageCount - siblingCount - pagesToEnd, lastPageIndex),
        ];
    }
    return [];
};

const Pagination = ({ onPageChange, totalCount, currentPage, pageSize, previousText = '<', nextText = '>', siblingCount = 1, ariaLabels = {
    paginationList: 'pagination list',
    page: 'page',
    previousPage: 'previous page',
    nextPage: 'next page',
}, className, }) => {
    const ellipsis = '…';
    const visibleItemsNumber = 3;
    const paginationRange = calculateItems({
        currentPage,
        totalCount,
        siblingCount,
        pageSize,
        ellipsis,
        visibleItemsNumber,
    });
    if (currentPage === 0 || paginationRange.length < 2) {
        return null;
    }
    const onNext = () => onPageChange(currentPage + 1);
    const onPrevious = () => onPageChange(currentPage - 1);
    const lastPage = paginationRange[paginationRange.length - 1];
    return (jsx("nav", Object.assign({ className: "cds-pagination", "aria-label": ariaLabels.paginationList }, { children: jsxs("ul", Object.assign({ className: classNames('cds-pagination__list', className) }, { children: [jsx("li", Object.assign({ className: classNames('cds-pagination__item cds-pagination__item--previous', {
                        'cds-pagination__item--disabled': currentPage === 1,
                    }) }, { children: jsx(Button, Object.assign({ variant: "nav", className: "cds-pagination__item--btn-nav", onClick: onPrevious, "aria-label": ariaLabels.previousPage, disabled: currentPage === 1 }, { children: previousText })) })), paginationRange.map((page, index) => (jsx("li", Object.assign({ className: classNames('cds-pagination__item', {
                        'cds-pagination__item--selected': page === currentPage,
                        'cds-pagination__item--ellipsis': page === ellipsis,
                    }) }, { children: page === ellipsis ? (page) : (jsx(Button, Object.assign({ variant: "nav", className: "cds-pagination__item--btn-nav", onClick: () => onPageChange(page), disabled: page === currentPage, "aria-label": `${ariaLabels.page} ${page}` }, { children: page }))) }), index))), jsx("li", Object.assign({ className: classNames('cds-pagination__item cds-pagination__item--next', {
                        'cds-pagination__item--disabled': currentPage >= lastPage,
                    }) }, { children: jsx(Button, Object.assign({ variant: "nav", className: "cds-pagination__item--btn-nav", onClick: onNext, "aria-label": ariaLabels.nextPage, disabled: currentPage >= lastPage }, { children: nextText })) }))] })) })));
};

export { Pagination };
